import * as z from 'zod';

export const GetCampaignRequest = z.object({
  campaignId: z
    .string()
    .describe('Campaign ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      optionIncludeStatistics: z
        .boolean()
        .describe(
          'Whether a returned campaign should include `publishingData.statistics`.\n\nDefault: `false`.'
        )
        .optional(),
    })
    .describe('Options to use when getting a campaign.')
    .optional(),
});
export const GetCampaignResponse = z.object({
  campaignId: z
    .string()
    .describe('Campaign ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  title: z
    .string()
    .describe(
      "Marketing campaign title. If you don't provide the value, the title automatically gets the value of an email subject."
    )
    .optional()
    .nullable(),
  firstImageUrl: z
    .string()
    .describe('URL of the first image after the logo.')
    .url()
    .optional()
    .nullable(),
  editorType: z
    .enum(['UNKNOWN', 'WEB', 'MOBILE', 'MJML'])
    .describe('Editor type.')
    .optional(),
  status: z
    .enum(['UNKNOWN', 'ACTIVE', 'ARCHIVED', 'DELETED'])
    .describe('Campaign status.')
    .optional(),
  visibilityStatus: z
    .enum(['UNKNOWN', 'DRAFT', 'PUBLISHED', 'TEMPLATE'])
    .describe('Campaign visibility status.')
    .optional(),
  distributionStatus: z
    .enum([
      'UNKNOWN',
      'NOT_STARTED',
      'SCHEDULED',
      'IN_DETECTION',
      'IN_MODERATION',
      'SAMPLING',
      'SENDING',
      'REJECTED',
      'TERMINATED',
      'DISTRIBUTED',
      'PAUSED',
    ])
    .describe('Campaign distribution status.')
    .optional(),
  publishingData: z
    .object({
      landingPageUrl: z.string().describe('Landing page URL.').url().optional(),
      statistics: z
        .object({
          landingPage: z
            .object({
              opened: z
                .number()
                .int()
                .describe(
                  'Number of times when a landing page was loaded or viewed.'
                )
                .optional(),
              clicked: z
                .number()
                .int()
                .describe(
                  'Number of times a link was clicked in a landing page.'
                )
                .optional(),
            })
            .describe('Landing page statistics of a marketing campaign.')
            .optional(),
          emailCampaign: z
            .object({
              delivered: z
                .number()
                .int()
                .describe('Number of delivered emails.')
                .optional(),
              opened: z
                .number()
                .int()
                .describe(
                  'Number of recipients who opened an email.\n\nIf the same recipient opened the same email twice, it is counted as a one unique open.'
                )
                .optional(),
              clicked: z
                .number()
                .int()
                .describe(
                  'Number of recipients who clicked a link in the email.\n\nIf the same recipient clicked the same link twice, it is counted as a one unique click.\n\nIf the same recipient clicked different links, it is counted as a one unique click.'
                )
                .optional(),
              bounced: z
                .number()
                .int()
                .describe("Number of emails that didn't reach the recipient.")
                .optional(),
              complained: z
                .number()
                .int()
                .describe(
                  'Number of recipients who reported the email as a spam.'
                )
                .optional(),
              notSent: z
                .number()
                .int()
                .describe(
                  "Number of emails that weren't sent due to the campaign termination."
                )
                .optional(),
            })
            .describe('Email distribution statistics of a marketing campaign.')
            .optional(),
          total: z
            .object({
              mailsSent: z
                .number()
                .int()
                .describe('Total number of emails sent.')
                .optional(),
              opened: z
                .number()
                .int()
                .describe('Number of landing page views and opened emails.')
                .optional(),
              clicked: z
                .number()
                .int()
                .describe('Number of landing page and email link clicks.')
                .optional(),
            })
            .describe(
              'Combined statistics of landing page and email distribution.'
            )
            .optional(),
        })
        .describe('Marketing campaign statistics.')
        .optional(),
      datePublished: z
        .date()
        .describe('Marketing campaign publish date.')
        .optional()
        .nullable(),
    })
    .describe(
      "Publishing data. The field is returned empty if the campaign isn't published."
    )
    .optional(),
  dateCreated: z
    .date()
    .describe('Date and time when the campaign was created.')
    .optional()
    .nullable(),
  dateUpdated: z
    .date()
    .describe('Date and time when the campaign was updated.')
    .optional()
    .nullable(),
  emailSubject: z
    .string()
    .describe(
      'Email subject of the campaign (initially copied from the template).'
    )
    .max(1000)
    .optional(),
});
export const ListCampaignsRequest = z.object({
  options: z
    .object({
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of items to load.')
            .min(0)
            .optional()
            .nullable(),
          offset: z
            .number()
            .int()
            .describe('Number of items to skip in the current sort order.')
            .min(0)
            .optional()
            .nullable(),
        })
        .describe('Paging parameters.')
        .optional(),
      optionIncludeStatistics: z
        .boolean()
        .describe(
          'Whether to include `publishingData.statistics` in the returned campaign object.\n\nDefault: `false`.'
        )
        .optional(),
      statuses: z
        .array(z.enum(['UNKNOWN', 'ACTIVE', 'ARCHIVED', 'DELETED']))
        .optional(),
      visibilityStatuses: z
        .array(z.enum(['UNKNOWN', 'DRAFT', 'PUBLISHED', 'TEMPLATE']))
        .optional(),
    })
    .describe('Options to use when getting the list of campaigns.')
    .optional(),
});
export const ListCampaignsResponse = z.object({
  paging: z
    .object({
      limit: z
        .number()
        .int()
        .describe('Number of items to load.')
        .min(0)
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Number of items to skip in the current sort order.')
        .min(0)
        .optional()
        .nullable(),
    })
    .describe('Paging parameters.')
    .optional(),
  campaigns: z
    .array(
      z.object({
        campaignId: z
          .string()
          .describe('Campaign ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        title: z
          .string()
          .describe(
            "Marketing campaign title. If you don't provide the value, the title automatically gets the value of an email subject."
          )
          .optional()
          .nullable(),
        firstImageUrl: z
          .string()
          .describe('URL of the first image after the logo.')
          .url()
          .optional()
          .nullable(),
        editorType: z
          .enum(['UNKNOWN', 'WEB', 'MOBILE', 'MJML'])
          .describe('Editor type.')
          .optional(),
        status: z
          .enum(['UNKNOWN', 'ACTIVE', 'ARCHIVED', 'DELETED'])
          .describe('Campaign status.')
          .optional(),
        visibilityStatus: z
          .enum(['UNKNOWN', 'DRAFT', 'PUBLISHED', 'TEMPLATE'])
          .describe('Campaign visibility status.')
          .optional(),
        distributionStatus: z
          .enum([
            'UNKNOWN',
            'NOT_STARTED',
            'SCHEDULED',
            'IN_DETECTION',
            'IN_MODERATION',
            'SAMPLING',
            'SENDING',
            'REJECTED',
            'TERMINATED',
            'DISTRIBUTED',
            'PAUSED',
          ])
          .describe('Campaign distribution status.')
          .optional(),
        publishingData: z
          .object({
            landingPageUrl: z
              .string()
              .describe('Landing page URL.')
              .url()
              .optional(),
            statistics: z
              .object({
                landingPage: z
                  .object({
                    opened: z
                      .number()
                      .int()
                      .describe(
                        'Number of times when a landing page was loaded or viewed.'
                      )
                      .optional(),
                    clicked: z
                      .number()
                      .int()
                      .describe(
                        'Number of times a link was clicked in a landing page.'
                      )
                      .optional(),
                  })
                  .describe('Landing page statistics of a marketing campaign.')
                  .optional(),
                emailCampaign: z
                  .object({
                    delivered: z
                      .number()
                      .int()
                      .describe('Number of delivered emails.')
                      .optional(),
                    opened: z
                      .number()
                      .int()
                      .describe(
                        'Number of recipients who opened an email.\n\nIf the same recipient opened the same email twice, it is counted as a one unique open.'
                      )
                      .optional(),
                    clicked: z
                      .number()
                      .int()
                      .describe(
                        'Number of recipients who clicked a link in the email.\n\nIf the same recipient clicked the same link twice, it is counted as a one unique click.\n\nIf the same recipient clicked different links, it is counted as a one unique click.'
                      )
                      .optional(),
                    bounced: z
                      .number()
                      .int()
                      .describe(
                        "Number of emails that didn't reach the recipient."
                      )
                      .optional(),
                    complained: z
                      .number()
                      .int()
                      .describe(
                        'Number of recipients who reported the email as a spam.'
                      )
                      .optional(),
                    notSent: z
                      .number()
                      .int()
                      .describe(
                        "Number of emails that weren't sent due to the campaign termination."
                      )
                      .optional(),
                  })
                  .describe(
                    'Email distribution statistics of a marketing campaign.'
                  )
                  .optional(),
                total: z
                  .object({
                    mailsSent: z
                      .number()
                      .int()
                      .describe('Total number of emails sent.')
                      .optional(),
                    opened: z
                      .number()
                      .int()
                      .describe(
                        'Number of landing page views and opened emails.'
                      )
                      .optional(),
                    clicked: z
                      .number()
                      .int()
                      .describe('Number of landing page and email link clicks.')
                      .optional(),
                  })
                  .describe(
                    'Combined statistics of landing page and email distribution.'
                  )
                  .optional(),
              })
              .describe('Marketing campaign statistics.')
              .optional(),
            datePublished: z
              .date()
              .describe('Marketing campaign publish date.')
              .optional()
              .nullable(),
          })
          .describe(
            "Publishing data. The field is returned empty if the campaign isn't published."
          )
          .optional(),
        dateCreated: z
          .date()
          .describe('Date and time when the campaign was created.')
          .optional()
          .nullable(),
        dateUpdated: z
          .date()
          .describe('Date and time when the campaign was updated.')
          .optional()
          .nullable(),
        emailSubject: z
          .string()
          .describe(
            'Email subject of the campaign (initially copied from the template).'
          )
          .max(1000)
          .optional(),
      })
    )
    .optional(),
});
export const PublishCampaignRequest = z.object({
  campaignId: z
    .string()
    .describe('Campaign ID')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      emailDistributionOptions: z
        .object({
          emailSubject: z
            .string()
            .describe(
              'The subject line for the email campaign, which appears in recipients’ inboxes.'
            )
            .max(1000)
            .optional()
            .nullable(),
          labelIds: z.array(z.string()).optional(),
          contactIds: z.array(z.string()).optional(),
          filterIds: z.array(z.string()).optional(),
          sendAt: z
            .date()
            .describe(
              'Campaign scheduling. You can schedule the campaign no less than 30 minutes until the sending time. [Upgrade your plan](https://support.wix.com/en/article/email-marketing-upgrading-your-email-marketing-plan) to schedule the campaign.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Email distribution options. Pass this object in the request if you need to distribute your marketing campaign with emails.'
        )
        .optional(),
    })
    .describe('Options to use when publishing a campaign.')
    .optional(),
});
export const PublishCampaignResponse = z.object({
  publishingData: z
    .object({
      landingPageUrl: z.string().describe('Landing page URL.').url().optional(),
      statistics: z
        .object({
          landingPage: z
            .object({
              opened: z
                .number()
                .int()
                .describe(
                  'Number of times when a landing page was loaded or viewed.'
                )
                .optional(),
              clicked: z
                .number()
                .int()
                .describe(
                  'Number of times a link was clicked in a landing page.'
                )
                .optional(),
            })
            .describe('Landing page statistics of a marketing campaign.')
            .optional(),
          emailCampaign: z
            .object({
              delivered: z
                .number()
                .int()
                .describe('Number of delivered emails.')
                .optional(),
              opened: z
                .number()
                .int()
                .describe(
                  'Number of recipients who opened an email.\n\nIf the same recipient opened the same email twice, it is counted as a one unique open.'
                )
                .optional(),
              clicked: z
                .number()
                .int()
                .describe(
                  'Number of recipients who clicked a link in the email.\n\nIf the same recipient clicked the same link twice, it is counted as a one unique click.\n\nIf the same recipient clicked different links, it is counted as a one unique click.'
                )
                .optional(),
              bounced: z
                .number()
                .int()
                .describe("Number of emails that didn't reach the recipient.")
                .optional(),
              complained: z
                .number()
                .int()
                .describe(
                  'Number of recipients who reported the email as a spam.'
                )
                .optional(),
              notSent: z
                .number()
                .int()
                .describe(
                  "Number of emails that weren't sent due to the campaign termination."
                )
                .optional(),
            })
            .describe('Email distribution statistics of a marketing campaign.')
            .optional(),
          total: z
            .object({
              mailsSent: z
                .number()
                .int()
                .describe('Total number of emails sent.')
                .optional(),
              opened: z
                .number()
                .int()
                .describe('Number of landing page views and opened emails.')
                .optional(),
              clicked: z
                .number()
                .int()
                .describe('Number of landing page and email link clicks.')
                .optional(),
            })
            .describe(
              'Combined statistics of landing page and email distribution.'
            )
            .optional(),
        })
        .describe('Marketing campaign statistics.')
        .optional(),
      datePublished: z
        .date()
        .describe('Marketing campaign publish date.')
        .optional()
        .nullable(),
    })
    .describe('Publishing info.')
    .optional(),
});
export const SendTestRequest = z.object({
  campaignId: z
    .string()
    .describe('Campaign ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      emailSubject: z
        .string()
        .describe('Email subject.')
        .max(1000)
        .optional()
        .nullable(),
      toEmailAddress: z.string().describe('Recipient email address.').email(),
    })
    .describe('Options for sending a test email.'),
});
export const SendTestResponse = z.object({});
export const PauseSchedulingRequest = z.object({
  campaignId: z
    .string()
    .describe('Campaign ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const PauseSchedulingResponse = z.object({});
export const RescheduleRequest = z.object({
  campaignId: z
    .string()
    .describe('ID of the campaign to reschedule.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  sendAt: z.date().describe('New time for sending out the campaign.'),
});
export const RescheduleResponse = z.object({});
export const DeleteCampaignRequest = z.object({
  campaignId: z
    .string()
    .describe('Campaign ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteCampaignResponse = z.object({});
export const ReuseCampaignRequest = z.object({
  campaignId: z
    .string()
    .describe('ID of the message to be duplicated.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const ReuseCampaignResponse = z.object({
  campaign: z
    .object({
      campaignId: z
        .string()
        .describe('Campaign ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      title: z
        .string()
        .describe(
          "Marketing campaign title. If you don't provide the value, the title automatically gets the value of an email subject."
        )
        .optional()
        .nullable(),
      firstImageUrl: z
        .string()
        .describe('URL of the first image after the logo.')
        .url()
        .optional()
        .nullable(),
      editorType: z
        .enum(['UNKNOWN', 'WEB', 'MOBILE', 'MJML'])
        .describe('Editor type.')
        .optional(),
      status: z
        .enum(['UNKNOWN', 'ACTIVE', 'ARCHIVED', 'DELETED'])
        .describe('Campaign status.')
        .optional(),
      visibilityStatus: z
        .enum(['UNKNOWN', 'DRAFT', 'PUBLISHED', 'TEMPLATE'])
        .describe('Campaign visibility status.')
        .optional(),
      distributionStatus: z
        .enum([
          'UNKNOWN',
          'NOT_STARTED',
          'SCHEDULED',
          'IN_DETECTION',
          'IN_MODERATION',
          'SAMPLING',
          'SENDING',
          'REJECTED',
          'TERMINATED',
          'DISTRIBUTED',
          'PAUSED',
        ])
        .describe('Campaign distribution status.')
        .optional(),
      publishingData: z
        .object({
          landingPageUrl: z
            .string()
            .describe('Landing page URL.')
            .url()
            .optional(),
          statistics: z
            .object({
              landingPage: z
                .object({
                  opened: z
                    .number()
                    .int()
                    .describe(
                      'Number of times when a landing page was loaded or viewed.'
                    )
                    .optional(),
                  clicked: z
                    .number()
                    .int()
                    .describe(
                      'Number of times a link was clicked in a landing page.'
                    )
                    .optional(),
                })
                .describe('Landing page statistics of a marketing campaign.')
                .optional(),
              emailCampaign: z
                .object({
                  delivered: z
                    .number()
                    .int()
                    .describe('Number of delivered emails.')
                    .optional(),
                  opened: z
                    .number()
                    .int()
                    .describe(
                      'Number of recipients who opened an email.\n\nIf the same recipient opened the same email twice, it is counted as a one unique open.'
                    )
                    .optional(),
                  clicked: z
                    .number()
                    .int()
                    .describe(
                      'Number of recipients who clicked a link in the email.\n\nIf the same recipient clicked the same link twice, it is counted as a one unique click.\n\nIf the same recipient clicked different links, it is counted as a one unique click.'
                    )
                    .optional(),
                  bounced: z
                    .number()
                    .int()
                    .describe(
                      "Number of emails that didn't reach the recipient."
                    )
                    .optional(),
                  complained: z
                    .number()
                    .int()
                    .describe(
                      'Number of recipients who reported the email as a spam.'
                    )
                    .optional(),
                  notSent: z
                    .number()
                    .int()
                    .describe(
                      "Number of emails that weren't sent due to the campaign termination."
                    )
                    .optional(),
                })
                .describe(
                  'Email distribution statistics of a marketing campaign.'
                )
                .optional(),
              total: z
                .object({
                  mailsSent: z
                    .number()
                    .int()
                    .describe('Total number of emails sent.')
                    .optional(),
                  opened: z
                    .number()
                    .int()
                    .describe('Number of landing page views and opened emails.')
                    .optional(),
                  clicked: z
                    .number()
                    .int()
                    .describe('Number of landing page and email link clicks.')
                    .optional(),
                })
                .describe(
                  'Combined statistics of landing page and email distribution.'
                )
                .optional(),
            })
            .describe('Marketing campaign statistics.')
            .optional(),
          datePublished: z
            .date()
            .describe('Marketing campaign publish date.')
            .optional()
            .nullable(),
        })
        .describe(
          "Publishing data. The field is returned empty if the campaign isn't published."
        )
        .optional(),
      dateCreated: z
        .date()
        .describe('Date and time when the campaign was created.')
        .optional()
        .nullable(),
      dateUpdated: z
        .date()
        .describe('Date and time when the campaign was updated.')
        .optional()
        .nullable(),
      emailSubject: z
        .string()
        .describe(
          'Email subject of the campaign (initially copied from the template).'
        )
        .max(1000)
        .optional(),
    })
    .describe('Campaign information.')
    .optional(),
});
export const GetAudienceRequest = z.object({
  campaignId: z
    .string()
    .describe('Campaign ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetAudienceResponse = z.object({
  audience: z
    .object({
      contactIds: z.array(z.string()).max(100).optional(),
      labelIds: z.array(z.string()).max(20).optional(),
      segmentIds: z.array(z.string()).max(20).optional(),
      contactsFilter: z
        .record(z.string(), z.any())
        .describe('Filter for contacts in JSON format.')
        .optional()
        .nullable(),
      contactsSearchTerm: z
        .string()
        .describe(
          'Contacts plain text search expression (searches in name, phone and email fields).'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      activeContactsOnly: z
        .boolean()
        .describe('Should "inactive" contacts be excluded or not.')
        .optional(),
    })
    .describe('Campaign audience.')
    .optional(),
});
export const IdentifySenderAddressRequest = z.object({
  emailAddress: z
    .string()
    .describe('Email address to verify.')
    .email()
    .min(6)
    .max(254),
});
export const IdentifySenderAddressResponse = z.object({
  senderAddress: z
    .string()
    .describe('Actual "from" address that will be used for sending emails.')
    .email()
    .min(6)
    .max(254)
    .optional(),
});
export const ValidateLinkRequest = z.object({
  url: z.string().describe('URL to validate.').min(1),
});
export const ValidateLinkResponse = z.object({
  valid: z.boolean().describe('Whether the link is valid.').optional(),
});
export const ValidateHtmlLinksRequest = z.object({
  html: z.string().describe('HTML string with links.').max(1000000),
});
export const ValidateHtmlLinksResponse = z.object({
  blacklistedLinks: z.array(z.string()).optional(),
});
export const ListStatisticsRequest = z.object({
  campaignIds: z.array(z.string()).min(1).max(100),
});
export const ListStatisticsResponse = z.object({
  statistics: z
    .array(
      z.object({
        campaignId: z
          .string()
          .describe('Campaign ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        landingPage: z
          .object({
            opened: z
              .number()
              .int()
              .describe(
                'Number of times when a landing page was loaded or viewed.'
              )
              .optional(),
            clicked: z
              .number()
              .int()
              .describe('Number of times a link was clicked in a landing page.')
              .optional(),
          })
          .describe('Landing page statistics.')
          .optional(),
        email: z
          .object({
            delivered: z
              .number()
              .int()
              .describe('Number of delivered emails.')
              .optional(),
            opened: z
              .number()
              .int()
              .describe(
                'Number of recipients who opened an email.\n\nIf the same recipient opened the same email twice, it is counted as a one unique open.'
              )
              .optional(),
            clicked: z
              .number()
              .int()
              .describe(
                'Number of recipients who clicked a link in the email.\n\nIf the same recipient clicked the same link twice, it is counted as a one unique click.\n\nIf the same recipient clicked different links, it is counted as a one unique click.'
              )
              .optional(),
            bounced: z
              .number()
              .int()
              .describe("Number of emails that didn't reach the recipient.")
              .optional(),
            complained: z
              .number()
              .int()
              .describe(
                'Number of recipients who reported the email as a spam.'
              )
              .optional(),
            notSent: z
              .number()
              .int()
              .describe(
                "Number of emails that weren't sent due to the campaign termination."
              )
              .optional(),
          })
          .describe('Email campaign statistics.')
          .optional(),
      })
    )
    .optional(),
});
export const ListRecipientsRequest = z.object({
  campaignId: z
    .string()
    .describe('Campaign ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  activity: z.enum([
    'DELIVERED',
    'OPENED',
    'CLICKED',
    'BOUNCED',
    'NOT_SENT',
    'SENT',
    'NOT_OPENED',
  ]),
  options: z
    .object({
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of items to load.')
            .min(0)
            .max(1000)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(1000)
            .optional()
            .nullable(),
        })
        .describe('Pagination options.')
        .optional(),
    })
    .describe('Options to use when getting the list of recipients.')
    .optional(),
});
export const ListRecipientsResponse = z.object({
  recipients: z
    .array(
      z.object({
        contactId: z
          .string()
          .describe('Contact ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        lastActivityDate: z
          .date()
          .describe('Date and time of the last activity.')
          .optional()
          .nullable(),
        emailAddress: z
          .string()
          .describe('Primary email address of the contact.')
          .email()
          .optional(),
        fullName: z
          .string()
          .describe('Full name of the contact (optional).')
          .max(100)
          .optional()
          .nullable(),
        contactDeleted: z
          .boolean()
          .describe('Is this contact currently deleted from the site or not.')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Details on the paged set of returned results.')
    .optional(),
});
